import { OPEN_BOOK_CALL_EVENT } from '@workspace/global-utils';
import { Config } from '@/utils/config';
import { getHostWindow, isSupportChatReady, openSupportChat } from '@/utils/support-chat';
import { isDocumentAgentPath } from '@/pages/routes-config';
import { defaultFalseBooleanAtomFamily, objectAtomFamily } from '@/jotai/atom';
import { atomKeys } from '@/jotai/atom-keys';
import Modal from '@/components/core/Modal';
import { Button } from '@workspace/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip';
import { SvgWhatsApp } from '@workspace/ui/components/icons/Icon';
import { cn } from '@workspace/ui/lib/utils';
import { buildSupportWhatsAppUrl } from '@workspace/global-utils';
import { Bug, Calendar, CircleHelp, MessageCircle } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useLocation } from 'react-router-dom';

interface AvailableActions {
  chat: boolean;
  feedback: boolean;
}

// `inline` renders a header-sized button instead of the floating launcher. The agent page uses it
// because the floating one sits on top of the composer's mic and send buttons.
export default function HelpLauncher({
  inline = false,
  buttonClassName,
}: {
  inline?: boolean;
  buttonClassName?: string;
}) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [isUserbackOpen, setIsUserbackOpen] = useState(false);
  const [isBookCallOpen, setIsBookCallOpen] = useState(false);
  const hasBoundUserbackEvents = useRef(false);

  const isStreaming = useAtomValue(defaultFalseBooleanAtomFamily(atomKeys.isStreaming));

  const isAgentPath = isDocumentAgentPath(pathname);
  // The builder zoom bar moves up to the launcher's spot while the AI streams, so hide the
  // launcher then (unless its menu is already open).
  const isHidden =
    !Config.HELP_LAUNCHER_ENABLED ||
    (!inline &&
      (isAgentPath ||
        pathname.startsWith('/template-preview/') ||
        pathname === '/export' ||
        pathname === '/preview' ||
        (isStreaming && !open)));

  useEffect(() => {
    if (isHidden) return;
    const openBookCall = () => setIsBookCallOpen(true);
    window.addEventListener(OPEN_BOOK_CALL_EVENT, openBookCall);
    return () => window.removeEventListener(OPEN_BOOK_CALL_EVENT, openBookCall);
  }, [isHidden]);

  // Binds Userback's open/close callbacks so the trigger button can dismiss an
  // already-open Userback panel. This only mutates hostWindow.Userback and a ref,
  // never React state, so it can't re-render this component. The menu-item
  // availability check (which does poll into state) lives in HelpMenuContent
  // instead, which only mounts while the popover is open — keeping that polling
  // out of this component is what stops it from interfering with the trigger's
  // own click handling.
  useEffect(() => {
    if (isHidden) return;

    const bindUserbackEvents = () => {
      const hostWindow = getHostWindow();
      if (hasBoundUserbackEvents.current || !hostWindow.Userback) return;

      const previousOnOpen = hostWindow.Userback.on_open;
      const previousOnClose = hostWindow.Userback.on_close;

      hostWindow.Userback.on_open = () => {
        previousOnOpen?.();
        setIsUserbackOpen(true);
      };

      hostWindow.Userback.on_close = () => {
        previousOnClose?.();
        setIsUserbackOpen(false);
      };

      hasBoundUserbackEvents.current = true;
    };

    bindUserbackEvents();
    const intervalId = window.setInterval(bindUserbackEvents, 1000);
    const hostWindow = getHostWindow();
    hostWindow.addEventListener('userback:ready', bindUserbackEvents);

    return () => {
      window.clearInterval(intervalId);
      hostWindow.removeEventListener('userback:ready', bindUserbackEvents);
    };
  }, [isHidden]);

  const handleLauncherClick = (event: MouseEvent<HTMLButtonElement>) => {
    // Radix runs this before its own open-toggle handler, so an uncaught throw here
    // (Chatwoot/Userback can still be mid-init right after a fresh page load) would
    // silently swallow the click instead of opening the menu.
    try {
      const hostWindow = getHostWindow();

      if (hostWindow.$chatwoot?.isOpen) {
        event.preventDefault();
        hostWindow.$chatwoot.toggle?.('close');
        return;
      }

      if (isUserbackOpen) {
        event.preventDefault();
        hostWindow.Userback?.close?.();
        setIsUserbackOpen(false);
      }
    } catch {
      // Fall through and let the popover open normally.
    }
  };

  if (isHidden) return null;

  const content = (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip open={open ? false : undefined}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              {inline ? (
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  className={buttonClassName}
                  aria-label="Open help menu"
                  data-ph-selector="help-launcher-open"
                  onClick={handleLauncherClick}
                >
                  <CircleHelp />
                </Button>
              ) : (
                <Button
                  type="button"
                  size="icon"
                  className={cn(
                    'size-11 rounded-full shadow-none transition-all duration-200',
                    open ? '' : 'bg-transparent',
                  )}
                  aria-label="Open help menu"
                  data-ph-selector="help-launcher-open"
                  onClick={handleLauncherClick}
                >
                  <img src="/images/help-question.svg" alt="" className="size-11" />
                </Button>
              )}
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side={inline ? 'bottom' : 'left'} sideOffset={8}>
            Need help?
          </TooltipContent>
        </Tooltip>

        <PopoverContent
          side={inline ? 'bottom' : 'top'}
          align="end"
          sideOffset={8}
          className="w-48 rounded-xl border-gray-100 p-1.5 shadow-[0_14px_36px_rgba(20,16,41,0.14)]"
        >
          <HelpMenuContent
            onFeedbackOpened={() => setIsUserbackOpen(true)}
            onBookCallOpened={() => setIsBookCallOpen(true)}
            onItemSelected={() => setOpen(false)}
          />
        </PopoverContent>
      </Popover>

      <Modal
        open={isBookCallOpen}
        onOpenChange={setIsBookCallOpen}
        title="Book a call"
        headerClassName="sr-only"
        className="h-full space-y-0 border-0! p-0! sm:max-w-5xl max-h-[90dvh] sm:max-h-[80dvh]"
      >
        <iframe
          src={Config.EXTERNAL_URLS.BOOK_A_CALL}
          title="Book a call"
          className="size-full rounded-lg border-0"
        />
      </Modal>
    </>
  );

  if (inline) return content;

  return (
    <div className="fixed z-29 transition-[right] duration-200 ease-out bottom-[max(calc(0.75rem_+_var(--mobile-agent-dock-height,0px)),calc(var(--guided-pill-offset,0px)_+_0.75rem))] sm:bottom-[max(3.5rem,calc(var(--guided-pill-offset,0px)_+_0.75rem))] right-[calc(0.75rem+var(--builder-right-sidebar-offset,0px))] sm:right-[calc(1rem+var(--builder-right-sidebar-offset,0px))]">
      {content}
    </div>
  );
}

// Rendered only while the popover is open (Radix unmounts PopoverContent while
// closed), so its polling and session-atom read never touch the always-mounted
// trigger.
function HelpMenuContent({
  onFeedbackOpened,
  onBookCallOpened,
  onItemSelected,
}: {
  onFeedbackOpened: () => void;
  onBookCallOpened: () => void;
  onItemSelected: () => void;
}) {
  const [availableActions, setAvailableActions] = useState<AvailableActions>({
    chat: false,
    feedback: false,
  });
  // Empty on the public view routes, which never run AuthLayout — the WhatsApp
  // message then falls back to a version without the email.
  const sessionUser = useAtomValue(objectAtomFamily(atomKeys.sessionUser)) as Partial<SessionUser>;

  useEffect(() => {
    const updateAvailableActions = () => {
      const hostWindow = getHostWindow();
      setAvailableActions({
        chat: isSupportChatReady(),
        feedback: Boolean(hostWindow.Userback?.open || hostWindow.Userback?.openForm),
      });
    };

    updateAvailableActions();
    const intervalId = window.setInterval(updateAvailableActions, 1000);
    const hostWindow = getHostWindow();
    hostWindow.addEventListener('chatwoot:ready', updateAvailableActions);
    hostWindow.addEventListener('userback:ready', updateAvailableActions);

    return () => {
      window.clearInterval(intervalId);
      hostWindow.removeEventListener('chatwoot:ready', updateAvailableActions);
      hostWindow.removeEventListener('userback:ready', updateAvailableActions);
    };
  }, []);

  const handleOpenChat = () => {
    openSupportChat(sessionUser);
    onItemSelected();
  };

  const handleOpenBookCall = () => {
    onBookCallOpened();
    onItemSelected();
  };

  const handleOpenFeedback = () => {
    const hostWindow = getHostWindow();
    const userback = hostWindow.Userback;

    if (userback?.open) {
      userback.open('bug', 'screenshot');
    } else {
      userback?.openForm?.('bug', 'screenshot');
    }

    onFeedbackOpened();
    onItemSelected();
  };

  return (
    <div className="flex flex-col gap-1">
      <Button
        type="button"
        variant="ghost"
        className="h-9 justify-start gap-2 rounded-lg px-2.5 text-sm font-medium"
        asChild
      >
        <a
          href={Config.SUPPORT_URLS.BASE}
          target="_blank"
          rel="noreferrer"
          data-ph-selector="help-launcher-help-center"
        >
          <CircleHelp className="size-4 text-primary" />
          Help center
        </a>
      </Button>

      <Button
        type="button"
        variant="ghost"
        className="h-9 justify-start gap-2 rounded-lg px-2.5 text-sm font-medium"
        onClick={handleOpenChat}
        disabled={!availableActions.chat}
        data-ph-selector="help-launcher-chat-support"
      >
        <MessageCircle className="size-4 text-primary" />
        Chat with support
      </Button>

      <Button
        type="button"
        variant="ghost"
        className="h-9 justify-start gap-2 rounded-lg px-2.5 text-sm font-medium"
        onClick={handleOpenBookCall}
        data-ph-selector="help-launcher-book-a-call"
      >
        <Calendar className="size-4 text-primary" />
        Book a call
      </Button>

      <Button
        type="button"
        variant="ghost"
        className="h-9 justify-start gap-2 rounded-lg px-2.5 text-sm font-medium"
        asChild
      >
        <a
          href={buildSupportWhatsAppUrl(sessionUser?.userEmail)}
          target="_blank"
          rel="noreferrer"
          data-ph-selector="help-launcher-whatsapp"
        >
          <SvgWhatsApp className="size-4 text-primary" />
          Chat on WhatsApp
        </a>
      </Button>

      <Button
        type="button"
        variant="ghost"
        className="h-9 justify-start gap-2 rounded-lg px-2.5 text-sm font-medium"
        onClick={handleOpenFeedback}
        disabled={!availableActions.feedback}
        data-ph-selector="help-launcher-report-issue"
      >
        <Bug className="size-4 text-primary" />
        Report an issue
      </Button>
    </div>
  );
}
